/**
 * Collection Infinite Scroll
 * Loads more products when user scrolls near the bottom of the collection page.
 * Works with Shopify's section rendering API and respects active filters/sorting.
 */
class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.isLoading = false;
    this.observer = null;
    this.sentinel = null;
  }

  connectedCallback() {
    if (this.currentPage >= this.totalPages) return;

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'collection-infinite-scroll__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.appendChild(this.sentinel);

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0
      }
    );
    this.observer.observe(this.sentinel);

    // Auto-load when page is short (low products per page) - check after layout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.checkAndLoadIfNeeded());
    });
  }

  isSentinelInView() {
    if (!this.sentinel || !this.sentinel.getClientRects().length) return false;
    const rect = this.sentinel.getBoundingClientRect();
    const margin = 200;
    return rect.top <= window.innerHeight + margin;
  }

  checkAndLoadIfNeeded() {
    if (this.isLoading || this.currentPage >= this.totalPages) return;
    if (this.isSentinelInView()) {
      this.loadNextPage().then(() => {
        this.checkAndLoadIfNeeded();
      });
    }
  }

  disconnectedCallback() {
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
    }
  }

  handleIntersection(entries) {
    if (this.isLoading || this.currentPage >= this.totalPages) return;

    const entry = entries[0];
    if (!entry.isIntersecting) return;

    this.loadNextPage();
  }

  getFetchUrl() {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', String(this.currentPage + 1));
    searchParams.set('section_id', this.sectionId);
    return `${window.location.pathname}?${searchParams.toString()}`;
  }

  async loadNextPage() {
    if (this.isLoading || this.currentPage >= this.totalPages) return;

    this.isLoading = true;
    this.classList.add('loading');

    try {
      const response = await fetch(this.getFetchUrl());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const container = doc.getElementById('ProductGridContainer');

      if (!container) {
        this.finishLoading();
        return;
      }

      const productGrid = container.querySelector('#product-grid');
      const newItems = productGrid ? productGrid.querySelectorAll('.grid__item') : [];

      if (newItems.length > 0) {
        const currentGrid = document.getElementById('product-grid');
        if (currentGrid) {
          newItems.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.querySelectorAll('[id]').forEach((el) => {
              el.id = `${el.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            });
            currentGrid.appendChild(clone);
          });

          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger(document);
          }
        }
      }

      this.currentPage += 1;
      this.dataset.currentPage = String(this.currentPage);

      if (this.currentPage >= this.totalPages) {
        this.observer?.unobserve(this.sentinel);
        this.classList.add('collection-infinite-scroll--complete');
      }
    } catch (error) {
      console.error('Collection infinite scroll error:', error);
    } finally {
      this.finishLoading();
    }
  }

  finishLoading() {
    this.isLoading = false;
    this.classList.remove('loading');
  }
}

customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
